import { z } from 'zod/v4';

import { MonetaryValueBackendRuntype, QuantityRuntype } from '../backendTypes';
import { SourcingContextRuntype } from '../dataSource/dataSourceBackendTypes';
import { ErrorCodeRuntype } from '../errorCodes';
import {
    ItemClassEnumRunType,
    NegotiationOfferIdRuntype,
    OfferRefRuntype,
    StandardPartOfferLinkedLocationTypeRunType,
} from '../offer';
import { PartLiteRuntype, PartLiteTypes } from '../part';
import { SupplierDTORuntype } from '../supplier';

export enum QuoteRequestLineItemStatus {
    NotSent = 'NotSent', // not sent quote request
    Sent = 'Sent', // sent quote request
    Overdue = 'Overdue', // overdue quote request
    Received = 'Received', // received quote request with offer
    NoBid = 'NoBid', // received quote request, but no received offer
    Discarded = 'Discarded', // discarded quote request
    Awarded = 'Awarded', // Quote request line item's offer is in awarded award scenario
    UnsolicitedOffer = 'UnsolicitedOffer', // Offer is present, but not linked to a quote request line item
}

export enum QuoteRequestStatus {
    NotSent = 'NotSent',
    Sent = 'Sent',
    Overdue = 'Overdue',
    Received = 'Received',
    Discarded = 'Discarded',
}

export enum QuoteRequestLineItemAssignmentReason {
    ManuallyAdded = 'ManuallyAdded',
    ReferenceSupplier = 'ReferenceSupplier',
    LineCard = 'LineCard',
    LineCardRule = 'LineCardRule',
    ApprovedVendorList = 'ApprovedVendorList',
}

export enum QuoteRequestLineItemApprovalStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
}

export type ApprovalStatusUpdatedBy = z.infer<typeof ApprovalStatusUpdatedByRuntype>;
export const ApprovalStatusUpdatedByRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Internal'),
        user_id: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        email: z.string(),
    }),
    z.object({
        type: z.literal('Supplier'),
        user_id: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        email: z.string(),
    }),
    z.object({ type: z.literal('DeletedUser') }),
]);

export type QuoteRequestLineItemApprovalStatusDTO = z.infer<typeof QuoteRequestLineItemApprovalStatusDTORuntype>;
export const QuoteRequestLineItemApprovalStatusDTORuntype = z.object({
    status: z.enum(QuoteRequestLineItemApprovalStatus),
    updated_by: ApprovalStatusUpdatedByRuntype.nullable(),
    updated_at: z.string().nullable(),
});

export type QuoteRequestLineItemSummaryDTO = z.infer<typeof QuoteRequestLineItemSummaryDTORuntype>;
export const QuoteRequestLineItemSummaryDTORuntype = z.object({
    id: z.number(),
    quote_request_id: z.string(),
    assignment_reason: z.enum(QuoteRequestLineItemAssignmentReason),
    status: z.enum(QuoteRequestLineItemStatus),
    approval_status: QuoteRequestLineItemApprovalStatusDTORuntype,
    approval_status_notes: z.string().nullable(),
});

export const EmailEventStatusRuntype = z.enum([
    'Queued',
    'Bounce',
    'Click',
    'Deferred',
    'Delivered',
    'Drop',
    'Open',
    'Processed',
    'SpamReport',
    'Unknown',
    'Unsubscribe',
]);
export type EmailEventStatus = z.infer<typeof EmailEventStatusRuntype>;

export type EmailHistoryEventDTO = z.infer<typeof EmailHistoryEventDTORuntype>;
export const EmailHistoryEventDTORuntype = z.object({
    id: z.string(),
    subject: z.string(),
    content: z.string(),
    sender_email: z.string(),
    recipients: z.array(z.string()),
    ccs: z.array(z.string()),
    sent_by: z.string().nullable(),
    created_at: z.string(),
    statuses: z.array(
        z.object({
            id: z.string(),
            status: EmailEventStatusRuntype.catch('Unknown'),
            created_at: z.string(),
            description: z
                .string()
                .optional()
                .nullable()
                .transform((val) => val ?? ''),
        }),
    ),
});

export const QuoteRequestFieldNameRuntype = z.enum([
    'AdditionalNotes',
    'Attachment',
    'Bid',
    'CancellationTimeUnit',
    'CancellationWindow',
    'PartCategory',
    'Customer',
    'PartDescription',
    'IpnDescription',
    'Ipn',
    'ItemClass',
    'LeadTime',
    'LeadTimeUnit',
    'RequestedManufacturer',
    'MinimumOrderQuantity',
    'MinimumPackagingQuantity',
    'RequestedMpn',
    'Ncnr',
    'NegotiatedByCustomer',
    'OfferedManufacturer',
    'NetPaymentDueDays',
    'EarlyPaymentDiscount',
    'EarlyPaymentDiscountDays',
    'OfferedMpn',
    'OneTimeCost',
    'Packaging',
    'PotentialQuantity',
    'Recipient',
    'RequiredQuantity',
    'DeliveryLotSize',
    'Stock',
    'SupplierPartNumber',
    'TargetPrice',
    'Unit',
    'UnitPrice',
    'UnitPriceCurrency',
    'UnitPriceQuantity',
    'ValidFor',
    'ValidFrom',
    'ValidUntil',
    'CountryOfOrigin',
    'EccnNumbers',
    'HtsCode',
    'Reach',
    'Rohs',
    'TotalOfferedQuantity',
    'BomCpn',
    'BomIpn',
    'IpnCpn',
    // these are client-only fields
    'RowStatus',
    'QuoteRequestLineItemStatus',
]);
export type QuoteRequestFieldName = z.infer<typeof QuoteRequestFieldNameRuntype>;

export type QuoteRequestDTO = z.infer<typeof QuoteRequestDTORuntype>;
export const QuoteRequestDTORuntype = z.object({
    id: z.string(),
    negotiation_id: z.number().nullable(),
    project: SourcingContextRuntype.nullable(),
    number: z.number(),
    supplier: SupplierDTORuntype,
    created_at: z.string(),
    discarded: z.boolean(),
    due_date: z.string().nullable().optional(),
    received_date: z.string().nullable().optional(),
    sent_date: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    line_item_count: z.number(),
    approval_status_pending_count: z.number(),
    approval_status_approved_count: z.number(),
    approval_status_rejected_count: z.number(),
    status: z.enum(QuoteRequestStatus),
    sent_by: z
        .object({
            id: z.string(),
            first_name: z.string(),
            last_name: z.string(),
            email: z.string(),
        })
        .nullable(),
    sent_to: z.array(
        z.object({
            id: z.number(),
            supplier_contact: z.string().nullable(),
            quote_request: z.string(),
            name: z.string().nullable(),
            email: z.string(),
        }),
    ),
    configuration: z
        .object({
            hidden_fields: z.array(QuoteRequestFieldNameRuntype),
            required_fields: z.array(QuoteRequestFieldNameRuntype),
            include_project_name: z.boolean(),
            include_customer_name: z.boolean(),
            include_pcb_specification: z.boolean().optional().default(true),
            include_panel_specification: z.boolean().optional().default(true),
            include_custom_part_attachments: z.boolean().optional().default(true),
            include_excel_quote_request: z.boolean().optional().default(true),
            include_exchange_rates: z.boolean().optional().default(true),
        })
        .optional(),
    email_log_history: z.array(EmailHistoryEventDTORuntype),
});

export type QuoteRequestLineItemDTO = z.infer<typeof QuoteRequestLineItemDTORuntype>;

/**
 * Relation between the requested part and the part the supplier offered.
 * Null when there is no received offer or no requested part to compare against.
 */
export enum OfferedPartStatus {
    Matching = 'Matching',
    PartAlternative = 'PartAlternative',
}

export const QuoteRequestLineItemKindRuntype = z.union([
    z.object({
        kind: z.literal('Original'),
        sourcing_line_item_id: z.uuid(),
    }),
    z.object({
        kind: z.literal('Alternative'),
        original_quote_request_line_item_id: z.number(),
        sourcing_line_item_id: z.uuid().nullable(),
    }),
    z.object({ kind: z.literal('Unlinked') }),
    z.object({ kind: z.literal('Legacy') }),
]);

/**
 * Where the requested part option came from, with the part option resolved to a full `PartLite`
 * rather than a bare id.
 */
export type QuoteRequestLineItemRequestedPartOriginDTO = z.infer<typeof QuoteRequestLineItemRequestedPartOriginRuntype>;
export const QuoteRequestLineItemRequestedPartOriginRuntype = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('BomItem'),
        part_option: PartLiteRuntype,
        bom_item_ipns: z.array(z.string()),
        bom_item_cpns: z.array(z.string()),
    }),
    z.object({
        kind: z.literal('PartDemand'),
        part_option: PartLiteRuntype,
        demand_id: z.uuid(),
    }),
]);

export const QuoteRequestLineItemDTORuntype = z.object({
    id: z.number(),
    kind: QuoteRequestLineItemKindRuntype,
    quote_request_id: z.string(),
    assignment_reason: z.enum(QuoteRequestLineItemAssignmentReason),
    customers: z.string().nullable().optional(), // Refactor that in the future
    recipients: z.string().nullable().optional(),
    negotiation_line_item_id: z.number().nullable(),
    ipn_description: z.string().nullable().optional(),
    part_description: z.string().nullable().optional(),
    target_price: MonetaryValueBackendRuntype.nullable().optional(),
    required_quantity: QuantityRuntype.nullable(),
    potential_quantity: QuantityRuntype.nullable(),
    offered_part_status: z.enum(OfferedPartStatus).nullable(),
    delivery_lot_size: QuantityRuntype.nullable(),
    requested_part: PartLiteRuntype.nullable().optional(),
    requested_part_origin: QuoteRequestLineItemRequestedPartOriginRuntype.nullable(),
    received_offer: z
        .object({
            offer_id: NegotiationOfferIdRuntype,
            part: PartLiteRuntype,
            linked_location_id: z.object({
                type: StandardPartOfferLinkedLocationTypeRunType,
                id: z.string(),
            }),
            unit_price: MonetaryValueBackendRuntype,
            unit_price_original_currency: MonetaryValueBackendRuntype,
            moq: z.number(),
            mpq: z.number(),
            cancellation_window_in_days: z.number().nullable(),
            valid_from: z.string().nullable(),
            valid_until: z.string().nullable(),
            item_class: ItemClassEnumRunType.nullable(),
        })
        .nullable(),
    linked_offer: OfferRefRuntype.nullable(),
    status: z.enum(QuoteRequestLineItemStatus),
    approval_status: QuoteRequestLineItemApprovalStatusDTORuntype,
    approval_status_notes: z.string().nullable(),
});

/**
 * The component the line item was requested through — an OTS component (whose id is its IPN) or
 * a custom component. Returns `null` for part options that are not a component, such as a bare
 * OTS part or a raw specification, since those have no component to originate from.
 *
 * Replaces the former `component_origin` field, which carried the IPN alone.
 */
export function getQuoteRequestLineItemComponentOrigin(
    lineItem: Pick<QuoteRequestLineItemDTO, 'requested_part_origin'>,
): string | null {
    const partOption = lineItem.requested_part_origin?.part_option;
    if (!partOption) {
        return null;
    }
    switch (partOption.kind) {
        case PartLiteTypes.Ipn:
        case PartLiteTypes.CustomComponent:
            return partOption.id;
        default:
            return null;
    }
}

export type QuoteRequestLineItemAggregatedStatusesDTO = z.infer<typeof QuoteRequestLineItemAggregatedStatusesRuntype>;
export const QuoteRequestLineItemAggregatedStatusesRuntype = z.object({
    overview: z.array(
        z.object({
            status: z.enum(QuoteRequestLineItemStatus),
            count: z.number(),
        }),
    ),
    statuses_by_negotiation_line_item: z.array(
        z.object({
            negotiation_line_item_id: z.number(),
            statuses: z.array(
                z.object({
                    status: z.enum(QuoteRequestLineItemStatus),
                    count: z.number(),
                }),
            ),
        }),
    ),
});

export type SupplierSelectionStrategyDTO = z.infer<typeof SupplierSelectionStrategyRuntype>;
export const SupplierSelectionStrategyRuntype = z.union([
    z.object({
        type: z.literal('SupplierPartTypeMatches'),
    }),
    z.object({
        type: z.literal('Manual'),
    }),
    z.object({
        type: z.literal('SupplierLineCard'),
        part_option_selection_strategy: z.union([z.literal('OnlyMatchingPartOptions'), z.literal('AllPartOptions')]),
        ignore_empty_line_card_supplier_matches: z.boolean(),
        include_empty_ipns: z.boolean(),
        include_generic_part_options: z.boolean(),
        include_raw_specification_parts: z.boolean(),
    }),
    z.object({
        type: z.literal('ApprovedVendorList'),
        part_option_selection_strategy: z.union([z.literal('OnlyMatchingPartOptions'), z.literal('AllPartOptions')]),
        ignore_empty_approved_vendor_list: z.boolean(),
        include_empty_ipns: z.boolean(),
        include_generic_part_options: z.boolean(),
        include_raw_specification_parts: z.boolean(),
    }),
    z.object({
        type: z.literal('ReferenceSupplier'),
        award_scenario_id: z.number(),
    }),
]);

export const SendQuoteRequestsResponseRuntype = z.object({
    results: z.array(
        z.object({
            quote_request_id: z.string(),
            status_code: z.number(),
            error_code: ErrorCodeRuntype.nullable(),
        }),
    ),
});
